import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  DataTable,
  Kpi,
  PageHead,
  Pager,
  RankedBars,
  StateBlock,
  type Column,
} from '@w7/ui'
import { useDownline, useMemberPnl, useSession } from '@/app/queries'
import { AccountLink, Amount, AsOf, Money, exportCsv } from '@/app/bits'
import { AccountCell } from '@/app/AppShell'
import { EMPTY, count, money, moneyCompact } from '@/lib/format'
import { ROLES, ROLE_LABEL, isAgent } from '@/data/types'
import {
  FilteredEmpty,
  ReportFilters,
  ScopeBanner,
  ScopeUnavailable,
  scopeSuffix,
  scopedCsvName,
  useReportFilters,
  type Dimension,
} from './ReportFilters'

/** The row shape comes from the hook, so this screen cannot drift from the query. */
type MemberPnlRow = NonNullable<ReturnType<typeof useMemberPnl>['data']>['rows'][number]

/**
 * Agents only. Every member's parent is an agent, so naming the parent costs
 * about sixteen rows rather than fetching the whole subtree a second time.
 */
const AGENT_QUERY = { roles: ROLES.filter(isAgent), pageSize: 200 }

/**
 * No tier chip, and the reason is structural: `getMemberPnl` aggregates
 * `world.pnl`, which is member-grain by construction, so every row here is
 * already a Member and the chip would offer exactly one option. Direction is
 * meaningless on a settled row. Game IS offered — `getMemberPnl` reads
 * `query.games`, so the chip is real rather than decorative.
 */
const DIMENSIONS: Dimension[] = ['range', 'scope', 'sport', 'game', 'member', 'q']

/** Eight rows at the RankedBars default row height — holds the layout while loading. */
const RANK_HEIGHT = 240

/** Table page size — matches My P&L; KPIs still use the full fetched set. */
const PAGE_SIZE = 25

const ZERO = { turnover: 0, memberPnl: 0, commission: 0, netPnl: 0, bets: 0 }

const sum = (rows: MemberPnlRow[]) =>
  rows.reduce(
    (acc, row) => ({
      turnover: acc.turnover + row.turnover,
      memberPnl: acc.memberPnl + row.memberPnl,
      commission: acc.commission + row.commission,
      netPnl: acc.netPnl + row.netPnl,
      bets: acc.bets + row.bets,
    }),
    ZERO,
  )

/**
 * Member P&L — replaces "PLStatement".
 *
 * The original put five columns side by side (Player, Master, SM, DM, TM), each
 * a bare number with an (i) icon and no legend anywhere on the page, so the only
 * way to learn what any of them meant was to ask someone. It also carried "Bet
 * Count" and "Multiplier" columns that both read 1 on every row of every report.
 * Here there is one number per member — their own result — and one number for
 * what that left the book with; the per-level split of the book's share is one
 * screen away on My P&L, as a labelled chart rather than five anonymous
 * integers. Bet count is the real count, and Multiplier is gone.
 *
 * Sign convention: `memberPnl` is the MEMBER's result, so negative means the
 * member lost, which is a win for the book. Every colour here is read from the
 * book's side — green is the book winning, red is the book paying out — which is
 * why member P&L renders inverted and the ranked bars are plotted negated.
 */
export function MemberPnlPage() {
  const filters = useReportFilters(DIMENSIONS)
  const { values, scopeAccount } = filters
  const range = values.range
  const navigate = useNavigate()

  /* Sport, game, member and scope all go INTO the query. The tiles, the two
     ranked-bar cards and the table then describe one set of rows rather than
     each narrowing a different way. */
  const { data, isLoading, dataUpdatedAt } = useMemberPnl({
    range: { from: range.from, to: range.to },
    ...(values.scope ? { rootId: values.scope } : {}),
    ...(values.sports.length ? { sports: values.sports } : {}),
    ...(values.games.length ? { games: values.games } : {}),
    ...(values.members.length ? { memberIds: values.members } : {}),
  })
  const { data: session } = useSession()
  const { data: agentPage } = useDownline(AGENT_QUERY)

  const agentName = useMemo(() => {
    const map = new Map<string, string>()
    // Members hanging directly off the signed-in agent have no row in the
    // downline result, because that result excludes the root. Their parent is
    // the viewer, so seed the map with them.
    if (session) map.set(session.account.id, session.account.username)
    for (const agent of agentPage?.rows ?? []) map.set(agent.id, agent.username)
    return map
  }, [agentPage, session])

  const rows = useMemo(() => data?.rows ?? [], [data])
  // Null while loading so the tiles render the em dash rather than a real ₹0.
  const totals = useMemo(() => (data ? sum(data.rows) : null), [data])
  const asOf = data?.asOf ?? (dataUpdatedAt || undefined)
  const commissionAvailable = data?.commissionAvailable ?? true

  const extremes = useMemo(() => {
    let best: MemberPnlRow | null = null
    let worst: MemberPnlRow | null = null
    for (const row of rows) {
      if (!best || row.memberPnl > best.memberPnl) best = row
      if (!worst || row.memberPnl < worst.memberPnl) worst = row
    }
    return {
      // A member who merely lost least is not a winner.
      winner: best && best.memberPnl > 0 ? best : null,
      loser: worst && worst.memberPnl < 0 ? worst : null,
    }
  }, [rows])

  const costliest = useMemo(
    () => rows.filter((r) => r.memberPnl > 0).sort((a, b) => b.memberPnl - a.memberPnl).slice(0, 8),
    [rows],
  )
  const heaviest = useMemo(
    () => rows.filter((r) => r.memberPnl < 0).sort((a, b) => a.memberPnl - b.memberPnl).slice(0, 8),
    [rows],
  )

  /* The one dimension `PnlQuery` cannot honour, so it stays client-side over
     rows already in hand — same behaviour the page always had, now URL-backed
     and therefore shareable. */
  const needle = values.q.trim().toLowerCase()
  const filtered = useMemo(
    () =>
      needle
        ? rows.filter(
            (r) =>
              r.account.username.toLowerCase().includes(needle) ||
              r.account.displayName.toLowerCase().includes(needle),
          )
        : rows,
    [rows, needle],
  )
  /* The sticky totals row reflects the filtered set (search), not only the
     current page — KPIs above describe the whole range. */
  const shown = useMemo(() => sum(filtered), [filtered])

  // Reset page when range or search changes (same pattern as My P&L).
  const pagerKey = `${range.from}|${range.to}|${needle}`
  const [pager, setPager] = useState({ key: pagerKey, page: 1 })
  const page = pager.key === pagerKey ? pager.page : 1
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE) || 1)
  const safePage = Math.min(page, pageCount)
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE
    return filtered.slice(start, start + PAGE_SIZE)
  }, [filtered, safePage])

  /* No sparkline column. This projection is one aggregate per member and carries
     no daily series, so a trend would need a second, slower query over every
     settled row in the range. A column that looks like a trend but is not one is
     worse than no column. */
  const columns = useMemo<Column<MemberPnlRow>[]>(
    () => [
      {
        id: 'member',
        header: 'Member',
        sortValue: (row) => row.account.username,
        total: `Total · ${count(filtered.length)} members`,
        cell: (row) => (
          <AccountCell
            username={row.account.username}
            displayName={row.account.displayName}
            role={row.account.role}
            to={`/member/${row.account.id}`}
          />
        ),
      },
      {
        id: 'agent',
        header: 'Agent',
        hideBelow: 1080,
        help: 'The agent this member sits directly beneath',
        sortValue: (row) =>
          row.account.parentId ? (agentName.get(row.account.parentId) ?? null) : null,
        cell: (row) => {
          const parentId = row.account.parentId
          const name = parentId ? agentName.get(parentId) : undefined
          return parentId && name ? <AccountLink id={parentId} username={name} /> : EMPTY
        },
      },
      {
        id: 'bets',
        header: 'Bets',
        numeric: true,
        hideBelow: 900,
        help: 'Settled bets in this range — the real count, not the constant 1 the old report printed',
        sortValue: (row) => row.bets,
        total: count(shown.bets),
        cell: (row) => count(row.bets),
      },
      {
        id: 'turnover',
        header: 'Turnover',
        numeric: true,
        hideBelow: 720,
        sortValue: (row) => row.turnover,
        total: <Amount value={shown.turnover} />,
        cell: (row) => <Amount value={row.turnover} />,
      },
      {
        id: 'memberPnl',
        header: 'Member P&L',
        numeric: true,
        help: 'The result the member themselves booked. Negative means they lost, which is a win for the book.',
        sortValue: (row) => row.memberPnl,
        total: <Money value={shown.memberPnl} signed invert />,
        cell: (row) => <Money value={row.memberPnl} signed invert />,
      },
      {
        id: 'commission',
        header: 'Commission',
        numeric: true,
        hideBelow: 1280,
        help: commissionAvailable
          ? 'Held back from winning bets, and already inside net to book'
          : 'analytics-service MemberPnlRow has no commission field — shown as zero until /commission (a6)',
        // Commission is never negative, so a green or red tone would carry no
        // information. It renders as a neutral amount.
        sortValue: (row) => row.commission,
        total: commissionAvailable ? <Amount value={shown.commission} /> : EMPTY,
        cell: (row) =>
          commissionAvailable ? <Amount value={row.commission} /> : EMPTY,
      },
      {
        id: 'netPnl',
        header: 'Net to book',
        numeric: true,
        // Last column to go. Below 560px only the member and their own result
        // survive, because a money table that scrolls sideways on a phone is
        // unreadable — the row you are scrolling leaves the screen.
        hideBelow: 560,
        help: 'Commission less the member result — what this member actually left you with',
        sortValue: (row) => row.netPnl,
        total: <Money value={shown.netPnl} signed />,
        cell: (row) => <Money value={row.netPnl} signed />,
      },
    ],
    [agentName, commissionAvailable, filtered.length, shown],
  )


  const winner = extremes.winner
  const loser = extremes.loser

  const download = () =>
    exportCsv(
      scopedCsvName('member-pnl', scopeAccount, range),
      filtered.map((row) => ({
        Member: row.account.username,
        Name: row.account.displayName,
        Role: ROLE_LABEL[row.account.role],
        Agent: (row.account.parentId ? agentName.get(row.account.parentId) : undefined) ?? '',
        Bets: row.bets,
        Turnover: row.turnover,
        'Member P&L': row.memberPnl,
        Commission: row.commission,
        'Net to book': row.netPnl,
      })),
    )

  const head = (
    <PageHead
      title="Member P&L"
      subtitle={`Who is winning and losing beneath you · ${range.label}${scopeSuffix(scopeAccount)}`}
    />
  )

  const bar = (
    <ReportFilters
      state={filters}
      memberSource="settled"
      textLabel="Filter by member username or name"
      textPlaceholder="Member username or name"
      omission="Tier and direction are not offered here: every row is one member’s own result, so a tier chip would have exactly one option, and a settled row has no give or take direction."
      actions={
        <>
          <AsOf at={asOf} />
          <Button
            icon="download"
            variant="secondary"
            onClick={download}
            disabled={filtered.length === 0}
          >
            Export CSV
          </Button>
        </>
      }
    />
  )

  if (filters.scopeInvalid) {
    return (
      <>
        {head}
        <ScopeUnavailable onClear={() => filters.clearDimension('scope')} />
      </>
    )
  }

  const provenance = scopeAccount ? (
    <ScopeBanner account={scopeAccount} onClear={() => filters.clearDimension('scope')} />
  ) : null

  /* Nothing settled at all is a different situation from nothing matching a
     filter, and it deserves instructions rather than an empty table. */
  if (data && data.rows.length === 0 && !filters.isFiltered) {
    return (
      <>
        {head}
        {bar}
        {provenance}
        <StateBlock
          icon="pnl"
          title="No member settled a bet in this range"
          actions={
            <>
              <Button variant="secondary" icon="turnover" onClick={() => navigate('/reports/turnover')}>
                See turnover
              </Button>
              <Button variant="secondary" icon="exposure" onClick={() => navigate('/exposure')}>
                Open bets
              </Button>
            </>
          }
        >
          Every member beneath you who settles a bet in {range.label} appears here, with what they
          won or lost and what it left you with. Widen the date range, or check Exposure for bets
          that are still running.
        </StateBlock>
      </>
    )
  }

  return (
    <>
      {head}
      {bar}
      {provenance}

      {/* ---------------------------------------------------------- KPIs -- */}
      <div className="w7-grid w7-grid-6" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Members who settled"
          value={data ? count(data.rows.length) : EMPTY}
          /* Under a scope this denominator has to be the SCOPED account's member
             count, or the tile reads "12 of 480" where 480 is your book and 12
             is theirs. */
          meta={
            [
              scopeAccount
                ? `of ${count(scopeAccount.memberCount)} beneath ${scopeAccount.username}`
                : session
                  ? `of ${count(session.account.memberCount)} beneath you`
                  : null,
              data?.martStale ? 'Mart marked stale' : null,
            ]
              .filter(Boolean)
              .join(' · ') || undefined
          }
        />
        <Kpi
          label="Total member P&L"
          value={totals ? moneyCompact(totals.memberPnl) : EMPTY}
          exact={money(totals?.memberPnl)}
          accent
          tone={
            totals
              ? totals.memberPnl === 0
                ? 'neutral'
                : totals.memberPnl < 0
                  ? 'profit'
                  : 'loss'
              : undefined
          }
          meta={
            totals
              ? totals.memberPnl < 0
                ? 'Members lost overall — a negative figure here is the book winning'
                : totals.memberPnl > 0
                  ? 'Members won overall — a positive figure here is the book paying out'
                  : 'Members finished level across this range'
              : undefined
          }
        />
        <Kpi
          label="Biggest winner"
          value={winner ? moneyCompact(winner.memberPnl) : EMPTY}
          exact={money(winner?.memberPnl)}
          tone={winner ? 'loss' : undefined}
          meta={
            winner
              ? `${winner.account.username} · ${count(winner.bets)} bets`
              : data
                ? 'No member finished ahead'
                : undefined
          }
          onClick={winner ? () => navigate(`/member/${winner.account.id}`) : undefined}
        />
        <Kpi
          label="Biggest loser"
          value={loser ? moneyCompact(loser.memberPnl) : EMPTY}
          exact={money(loser?.memberPnl)}
          tone={loser ? 'profit' : undefined}
          meta={
            loser
              ? `${loser.account.username} · ${count(loser.bets)} bets`
              : data
                ? 'No member finished behind'
                : undefined
          }
          onClick={loser ? () => navigate(`/member/${loser.account.id}`) : undefined}
        />
        <Kpi
          label="Commission"
          value={
            !data
              ? EMPTY
              : commissionAvailable
                ? moneyCompact(totals?.commission ?? 0)
                : EMPTY
          }
          exact={commissionAvailable ? money(totals?.commission) : undefined}
          meta={
            commissionAvailable
              ? 'Held back from winning bets'
              : 'Not on /member-pnl — ledger commission is slice a6 (/commission)'
          }
        />
        <Kpi
          label="Turnover"
          value={totals ? moneyCompact(totals.turnover) : EMPTY}
          exact={money(totals?.turnover)}
          meta={totals ? `${count(totals.bets)} settled bets` : undefined}
        />
      </div>

      {/* --------------------------------------------------------- ranks -- */}
      <div className="w7-grid w7-grid-2" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card
          title="Members costing you the most"
          subtitle="What the book paid out to each, biggest first"
        >
          {data ? (
            <RankedBars
              label="Members the book paid out to, largest first"
              byTone
              // Plotted from the book's side — a member's win is the book's
              // payout — so the bar colour obeys the one rule on this console.
              rows={costliest.map((row) => ({
                label: row.account.username,
                value: -row.memberPnl,
                note: `${count(row.bets)} bets`,
                onClick: () => navigate(`/member/${row.account.id}`),
              }))}
              format={(value) => moneyCompact(value)}
              emptyMessage="No member finished this range ahead"
            />
          ) : (
            <div style={{ height: RANK_HEIGHT }} />
          )}
        </Card>

        <Card title="Members losing the most" subtitle="What the book won from each, biggest first">
          {data ? (
            <RankedBars
              label="Members the book won from, largest first"
              byTone
              rows={heaviest.map((row) => ({
                label: row.account.username,
                value: -row.memberPnl,
                note: `${count(row.bets)} bets`,
                onClick: () => navigate(`/member/${row.account.id}`),
              }))}
              format={(value) => moneyCompact(value)}
              emptyMessage="No member finished this range behind"
            />
          ) : (
            <div style={{ height: RANK_HEIGHT }} />
          )}
        </Card>
      </div>

      {/* --------------------------------------------------------- table -- */}
      <Card
        title="Every member who settled"
        subtitle="Biggest winners first — paging is 25 per page; totals cover the filtered set. Sorting reorders rows on this page."
        flush
        /* Search lives in the filter bar, not on the card: two boxes filtering
           the same table is how an operator ends up typing in the one that is
           not wired. `pagerKey` carries the needle, so paging still resets. */
        footer={
          <Pager
            page={safePage}
            pageCount={pageCount}
            onChange={(next) => setPager({ key: pagerKey, page: next })}
            total={filtered.length}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Member profit and loss for the selected range"
          rows={pageRows}
          columns={columns}
          rowKey={(row) => row.account.id}
          loading={isLoading}
          skeletonRows={10}
          showTotals
          initialSort={{ columnId: 'memberPnl', direction: 'desc' }}
          onRowClick={(row) => navigate(`/member/${row.account.id}`)}
          empty={
            filters.isFiltered ? (
              <FilteredEmpty state={filters} />
            ) : (
              <StateBlock icon="pnl" title="No member settled a bet in this range">
                Only members who settled a bet in {range.label} are listed, so someone who has not
                bet in this range will not appear here whatever you filter by.
              </StateBlock>
            )
          }
        />
      </Card>
    </>
  )
}
